import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Globe, Github, Linkedin, Link2, Twitter } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const Wrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
  justify-content: center;
  margin-top: 1rem;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.85rem;
  border-radius: 9999px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.muted};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 0.95rem;
    height: 0.95rem;
  }
`;

const PLATFORM_META = {
  twitter: { label: 'Twitter', Icon: Twitter },
  linkedin: { label: 'LinkedIn', Icon: Linkedin },
  github: { label: 'GitHub', Icon: Github },
  website: { label: 'Website', Icon: Globe },
};

export default function AuthorSocialLinks({ socialLinks = {}, customLinks = [] }) {
  const items = [];

  Object.entries(PLATFORM_META).forEach(([key, meta]) => {
    const url = socialLinks?.[key];
    if (url) items.push({ key, url, label: meta.label, Icon: meta.Icon });
  });

  (customLinks || []).forEach((link, index) => {
    if (link?.url) {
      items.push({
        key: `custom-${index}`,
        url: link.url,
        label: link.label || link.url,
        Icon: Link2,
      });
    }
  });

  if (!items.length) return null;

  return (
    <Wrap>
      {items.map(({ key, url, label, Icon }) => (
        <SocialLink key={key} href={url} target="_blank" rel="noopener noreferrer">
          <Icon />
          {label}
        </SocialLink>
      ))}
    </Wrap>
  );
}

const BylineWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

const Avatar = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  overflow: hidden;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarLetter = styled.span`
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 700;
  font-size: 0.875rem;
`;

const AuthorLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }
`;

const PlainName = styled.span`
  font-weight: 600;
`;

export function AuthorByline({ name, slug, avatar, className }) {
  const { getAuthorUrl } = useLanguage();
  const displayName = name || 'Author';
  const initial = displayName.charAt(0).toUpperCase();

  const avatarNode = (
    <Avatar>
      {avatar ? <AvatarImg src={avatar} alt={displayName} /> : <AvatarLetter>{initial}</AvatarLetter>}
    </Avatar>
  );

  return (
    <BylineWrap className={className}>
      {slug ? (
        <AuthorLink to={getAuthorUrl(slug)} aria-label={`View ${displayName}'s profile`}>
          {avatarNode}
        </AuthorLink>
      ) : (
        avatarNode
      )}
      {slug ? (
        <AuthorLink to={getAuthorUrl(slug)}>{displayName}</AuthorLink>
      ) : (
        <PlainName>{displayName}</PlainName>
      )}
    </BylineWrap>
  );
}
